import React, { FC, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { format } from '../../../../utils/date'
import { useTranslation } from 'react-i18next'
import ExportModal from '../../../components/task/ExportModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ReactGA from 'react-ga4'
import { fetchTaskExportDetail, fetchTaskExportList } from '../../../../states/containers/task/taskSlice'
import Tips from '../../../components/common/Tips'
import LoadingSquare from '../../../components/common/LoadingSquare'

interface ExportModalState {
  display: boolean,
  type: string,
  id: number | undefined,
}

const Index: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    commonPart,
    task,
    login,
  } = useSelector((state: RootState) => (state))

  const [exportModalState, setExportModalState] = useState<ExportModalState>({
    display: false,
    type: "",
    id: undefined,
  })

  const [isListLoading, setIsListLoading] = useState<boolean>(true)

  useEffect(() => {
    if (!commonPart.company?.id) return
    dispatch(fetchTaskExportList())
  }, [commonPart])

  useEffect(() => {
    setIsListLoading(task.isLoading || false)
  }, [task.isLoading])

  useEffect(() => {
    setIsListLoading(true)
  }, [])

  const openExportModal = (id: number, type: string) => {
    setExportModalState({
      ...exportModalState,
      display: true,
      type: type,
      id: id,
    })
  }

  const closeExportModal = () => {
    setExportModalState({
      ...exportModalState,
      display: false,
    })
  }

  const reloadList = () => {
    dispatch(fetchTaskExportList())
  }

  const downloadFile = (id: number) => {
    dispatch(
      fetchTaskExportDetail(
        { id: id.toString() },
        (url) => {
          window.location.href = url
          ReactGA.event({
            category: 'TaskCsvExport',
            action: 'Download',
            label: id.toString(),
            value: 1,
          })
        },
      ),
    )
  }

  const displayList = () => (
    task.taskExport?.exports.length ? (
      task.taskExport?.exports.map((info) => (
        <tr key={info.export_id}>
          <td>
            {format(info.target_date, t('time.date.formats.export_target_date'))}
          </td>
          <td>
            {
              info.file_type === "attendance" && (
                <>
                  {`勤怠データ${info.is_only_approved ? "(承認済み)" : ""}`}
                </>
              )
            }
            {
              info.file_type === "project" && (
                <>
                  {info.label_group_name}
                </>
              )
            }
            {
              info.file_type === "project_all" && (
                <>
                  プロジェクトデータ一括
                </>
              )
            }
            {
              info.file_type === "category" && (
                <>
                  カテゴリーデータ
                </>
              )
            }
          </td>
          <td
            style={{
              gap: "2px",
            }}
          >
            {
              info.file_type !== "category" && (
                <div>
                  すべて
                </div>
              )
            }
            {
              info.file_type === "category" && (
                <>
                  <div
                    style={{
                      borderRadius: "4px",
                      backgroundColor: "#595959",
                      color: "#FFFFFF",
                      padding: "2px 4px",
                      fontSize: "9px",
                    }}
                  >
                    カテゴリー
                  </div>
                  {
                    info.columns?.includes("label_group") && (
                      <div
                        style={{
                          borderRadius: "4px",
                          backgroundColor: "#595959",
                          color: "#FFFFFF",
                          padding: "2px 4px",
                          fontSize: "9px",
                        }}
                      >
                        プロジェクト
                      </div>
                    )
                  }
                  {
                    info.columns?.includes("label") && (
                      <div
                        style={{
                          borderRadius: "4px",
                          backgroundColor: "#595959",
                          color: "#FFFFFF",
                          padding: "2px 4px",
                          fontSize: "9px",
                        }}
                      >
                        業務
                      </div>
                    )
                  }
                  {
                    info.columns?.includes("member") && (
                      <div
                        style={{
                          borderRadius: "4px",
                          backgroundColor: "#595959",
                          color: "#FFFFFF",
                          padding: "2px 4px",
                          fontSize: "9px",
                        }}
                      >
                        メンバー
                      </div>
                    )
                  }
                  {
                    info.columns?.includes("date") && (
                      <div
                        style={{
                          borderRadius: "4px",
                          backgroundColor: "#595959",
                          color: "#FFFFFF",
                          padding: "2px 4px",
                          fontSize: "9px",
                        }}
                      >
                        日付
                      </div>
                    )
                  }
                </>
              )
            }
          </td>
          <td>{info.code || "-"}</td>
          <td>
            {
              info.status === "complete" ? (
                <button type="button" className="download" onClick={() => downloadFile(info.export_id)}>
                  <FontAwesomeIcon
                    icon={['fas', 'file-csv']}
                    className="csv_icon"
                  />
                  {t('export.button.download')}
                </button>
              ) : (
                <div className="generating">
                  {t('export.list.generating')}
                </div>
              )
            }
          </td>
          <td>
            {
              info.status === "complete" && info.expire_date ? (
                format(info.expire_date, t('time.date.formats.export_expire_date'))
              ) : (
                "-"
              )
            }
          </td>
        </tr>
      ))
    ) : (
      <tr>
        <td className="no_data">
          <div className="text">
            {t('export.list.no_data')}
          </div>
          <button
            type="button"
            className="button add_one_member"
            onClick={(event) => openExportModal(Number(event.currentTarget.dataset.id), "create")}
            disabled={!login.permissions?.task_report_view && !login.permissions?.task_attendance_export}
          >
            {t('export.button.create')}
          </button>
        </td>
      </tr>
    )
  )

  return (
    <section className="export_page task_page">
      <Helmet>
        <title>Qasee - 工数管理 - データエクスポート</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        データエクスポート
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          データエクスポート
        </h1>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-header">
            <div className="description">
              <p>
                プロジェクトデータや勤怠データをCSV形式で出力することができます。
              </p>
              <p>
                ※ データ量が多い場合、作成に時間がかかることがあります。
              </p>
            </div>
            <div
              style={{
                display: "flex",
                gap: "16px",
              }}
            >
              <div className="button-area">
                <button
                  type="button"
                  className="reload"
                  onClick={reloadList}
                  style={{
                    padding: "7px 12px",
                  }}
                >
                  <Tips
                    description="最新の情報に更新"
                    className=""
                    icon={false}
                  >
                    <>
                      <FontAwesomeIcon icon={['fas', 'sync-alt']} />
                    </>
                  </Tips>
                </button>
              </div>
              <div className="button-area">
                <button
                  type="button"
                  className="button add_one_member"
                  onClick={(event) => openExportModal(Number(event.currentTarget.dataset.id), "create")}
                  disabled={!login.permissions?.task_report_view && !login.permissions?.task_attendance_export}
                >
                  {t('export.button.create')}
                </button>
              </div>
            </div>
          </div>
          <div className="block-detail">
            <table className="flex-table export_csv_table">
              <thead>
                <tr>
                  <th>{t('export.list.target_ym')}</th>
                  <th>プロジェクト名 / ファイル名</th>
                  <th>出力対象</th>
                  <th>{t('export.list.character_code')}</th>
                  <th>{t('export.list.status')}</th>
                  <th>{t('export.list.expired_at')}</th>
                </tr>
              </thead>
              <tbody>
                {
                  isListLoading || (
                    displayList()
                  )
                }
              </tbody>
            </table>
            {
              isListLoading && (
                <LoadingSquare>
                  <>
                    エクスポートリストを読み込んでいます…
                  </>
                </LoadingSquare>
              )
            }
          </div>
        </div>
      </section>
      {/* 編集モーダル */}
      <ExportModal
        exportModalState={exportModalState}
        closeCallback={closeExportModal}
      />
    </section>
  )
}
export default Index
